import type { FC, JSX } from 'react';

import styled from 'styled-components';
import { JourneyContractSegmentTransportType } from '@lmn/lmn-components';
import { Text, Tooltip } from '@lmn/design-system';
import { formatDateShortDayMonthIsomorphic, formatTimeTwoDigit } from '@/app/components/search/utils/dates/utilDates';
import { EvoFullTransportTimeAndLocation } from '@/app/components/hotel-detail/evo/transport/EvoTransportDetailsCard/EvoTransport/FullTransportTimeAndLocation';
import {
  calcDeltaDays,
  decorateAge,
  WayStops,
} from '@/app/components/hotel-detail/flight-train/TransportDetails/Transport/utils';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { EvoTransportTimeline } from '@/app/components/hotel-detail/evo/transport/EvoTransportDetailsCard/EvoTransport/EvoTransportTimeline';
import type { AirlineCompany } from '@/app/components/hotel-detail/evo/transport/EvoTransportDetailsCard/EvoTransport/EvoTransportTimeline';
import { Icon } from '@/app/common/icons/Icon';
import { EvoTransportContainer } from './styled';

export type EvoTransportProps = {
  isOutBound: boolean;
  departureDate: string;
  departureAirportCode: string;
  departureAirportName: string;
  arrivalDate: string;
  arrivalAirportCode: string;
  arrivalAirportName: string;
  providerCode: string;
  providerName: string;
  numberOfStops: number;
  durationInMinutes: number;
  locale: string;
  dictionary: Dictionary;
  differentDepartureAirportCodes: boolean;
  differentArrivalAirportCodes: boolean;
  companies?: AirlineCompany[];
};

const JourneySummary = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const TransportHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
`;

const DepartureDateCell = styled.div`
  display: flex;
  align-items: center;

  > img {
    border-radius: 4px;
    margin-right: 8px;
  }
`;

const DateLabel = styled(Text).attrs({ variant: 'Body' })`
  font-weight: bold;
`;

const ArrivalDateLabel = styled(Text).attrs({ variant: 'Body' })`
  color: ${({ theme }) => theme.kallisto.color.text.static.textWarning};
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 4px;
`;

const TimelineColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  [class*='TransportTimeline__TravelSummary'] {
    width: 100%;
  }
`;

const EvoTransport: FC<EvoTransportProps> = (props): JSX.Element => {
  const {
    isOutBound,
    providerCode,
    providerName,
    departureDate,
    departureAirportCode,
    departureAirportName,
    arrivalDate,
    arrivalAirportCode,
    arrivalAirportName,
    numberOfStops,
    durationInMinutes,
    locale,
    dictionary,
    differentArrivalAirportCodes,
    differentDepartureAirportCodes,
    companies,
  } = props;
  const { isTrain } = useUrlSearchParams();
  const age = decorateAge(durationInMinutes, dictionary);
  const plusDays = calcDeltaDays(departureDate, arrivalDate);

  const stops = WayStops({ numberOfStops, dictionary });
  const departureTransportInfoDateText = formatDateShortDayMonthIsomorphic(locale, departureDate);
  const arrivalTransportInfoDateText = plusDays > 0 ? formatDateShortDayMonthIsomorphic(locale, arrivalDate) : null;
  const departureTransportInfoTimeText = formatTimeTwoDigit(departureDate, locale);
  const arrivalTransportInfoTimeText = formatTimeTwoDigit(arrivalDate, locale);

  return (
    <EvoTransportContainer $isOutBound={isOutBound}>
      <TransportHeaderRow>
        <DepartureDateCell>
          <Icon
            category="Transportation"
            name={isTrain ? 'icon-train' : isOutBound ? 'icon-airplane-right' : 'icon-airplane-left'}
            size={20}
            color="iconPrimary"
            alt=""
          />
          <DateLabel>{departureTransportInfoDateText}</DateLabel>
        </DepartureDateCell>
        {arrivalTransportInfoDateText && (
          <Tooltip content={dictionary['widget.trip_card.way.arrival.next_day.tooltip']} placement="top">
            <ArrivalDateLabel>{arrivalTransportInfoDateText}</ArrivalDateLabel>
          </Tooltip>
        )}
      </TransportHeaderRow>
      <JourneySummary>
        <EvoFullTransportTimeAndLocation
          time={departureTransportInfoTimeText}
          location={{
            iataCode: departureAirportCode,
            label: departureAirportName,
            highlight: (isOutBound && differentDepartureAirportCodes) || (!isOutBound && differentArrivalAirportCodes),
            transportType: isTrain
              ? JourneyContractSegmentTransportType.TRAIN
              : JourneyContractSegmentTransportType.FLIGHT,
          }}
          isArrival={false}
        />

        <TimelineColumn>
          <EvoTransportTimeline
            duration={age}
            stops={[]}
            stopLabel={stops}
            showIcons={false}
            providerName={providerName}
            providerCode={providerCode}
            companies={companies}
          />
        </TimelineColumn>
        <EvoFullTransportTimeAndLocation
          time={arrivalTransportInfoTimeText}
          location={{
            iataCode: arrivalAirportCode,
            label: arrivalAirportName,
            highlight: (isOutBound && differentArrivalAirportCodes) || (!isOutBound && differentDepartureAirportCodes),
            transportType: isTrain
              ? JourneyContractSegmentTransportType.TRAIN
              : JourneyContractSegmentTransportType.FLIGHT,
          }}
          isArrival={true}
        />
      </JourneySummary>
    </EvoTransportContainer>
  );
};

export default EvoTransport;
